import type { TeacherClass } from 'src/class/class-provider';

import dayjs from 'dayjs';
import { useMemo, useState, useEffect } from 'react';

import { useTeacherDays } from 'src/sections/week/use-teacher-week';
import { useLiveSessionCounts } from 'src/sections/week/use-class-signals';

import { periodStates, type PeriodState } from './header-model';

// ----------------------------------------------------------------------

/** Minutes since midnight, re-read once a minute (no re-query — the day's data is cached). */
function useMinuteTick(): dayjs.Dayjs {
  const [now, setNow] = useState(() => dayjs());
  useEffect(() => {
    const id = window.setInterval(() => setNow(dayjs()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/**
 * The header's "now / next" and practicing counts (epic #617 P2). Today's meetings come from
 * `useTeacherDays` (same bell schedule, alternate schedules and holidays as My Week, shared
 * react-query cache); live counts from the `liveSessions` mirror, one chunked listener.
 */
export function useTodayPeriods(classes: TeacherClass[]): {
  states: Map<string, PeriodState>;
  practicing: Map<string, number>;
} {
  const states = useTodayStates(classes);
  const ids = useMemo(() => classes.map((c) => c.id), [classes]);
  const counts = useLiveSessionCounts(ids);
  const practicing = useMemo(() => new Map([...counts].map(([id, c]) => [id, c.live])), [counts]);

  return { states, practicing };
}

/** Today's period states only (no live-session listener) — the Wrap up card and nav badge (epic #647). */
export function useTodayStates(classes: TeacherClass[]): Map<string, PeriodState> {
  const now = useMinuteTick();
  const today = now.startOf('day');
  const { days } = useTeacherDays(classes, today, today);

  const nowMinutes = now.hour() * 60 + now.minute();
  const meetings = days[0]?.meetings;
  const states = useMemo(
    () =>
      periodStates(
        (meetings ?? []).map((m) => ({ classId: m.cls.id, startTime: m.period.startTime, endTime: m.period.endTime })),
        nowMinutes
      ),
    [meetings, nowMinutes]
  );
  return states;
}
